import React, {useMemo, useState} from 'react';
import dayjs from 'dayjs';
import {Box, Button, Stack, Typography} from '@mui/material';
import {Sx} from 'app/types/common';
import {useUnitPresets} from 'app/hooks/Device/Preset/useUnitPresets';
import {LoadingPlaceholder} from 'app/components/sharedReactComponents/LoadingPlaceholder';
import {
  DevicePresetForm,
  PresetFields,
} from 'app/components/sharedReactComponents/DevicePresetForm/DevicePresetForm';
import {formatFullDate} from 'app/util/time';
import {Edge} from 'app/domain/edge';
import {PresetCard} from 'app/components/sharedReactComponents/DevicePresetCard/PresetCard/PresetCard';
import {ModelService} from 'app/services/deviceModel/DeviceModelService';
import {PresetApiService} from 'app/services/api/preset/PresetApiService';
import {Notifications} from 'app/components/Notifications';
import {isError} from 'app/api/types';
import {EdgePopover} from 'app/components/widgets/billing';
import {AnyDeviceModelType} from 'app/components/DeviceDetails/Models/Fabric';
import {isNil} from 'app/util/isNil';
import {useCloud} from 'app/components/features/cloud';

interface Props extends Sx {
  unit: AnyDeviceModelType;
  teamPresets: Edge.TeamPreset[];
  reloadTeamPresets: () => Promise<any>;
}

export function UnitPresets({sx, teamPresets, unit, reloadTeamPresets}: Props) {
  const deviceId = unit.getId();
  const deviceName = unit.getName();
  const deviceModel = unit.getModelName();
  const offline = unit.isOffline();

  const {user} = useCloud();
  const [creating, setCreating] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const {
    data: presets,
    isSuccess,
    refetch: reloadLocalPresets,
  } = useUnitPresets({enabled: true, unitId: deviceId});

  const date = formatFullDate(dayjs().unix());

  const presetMap = useMemo(
    () => new Map<string, string>(teamPresets.map((p) => [p.hash, p.name])),
    [teamPresets],
  );

  const {caps} = unit;
  const capability = caps.presets;

  const getNameInTeam = (hash: string) => presetMap.get(hash);

  const handleCreatePreset = async (fields: PresetFields) => {
    const sections = fields.sections as Edge.PresetSection[];

    try {
      await PresetApiService.createUnitPreset(deviceId, {
        name: fields.name,
        sections,
        description:
          fields.description ??
          `This preset is loaded from ${deviceName} by ${user.name} on ${date}`,
      });

      setCreating(false);
    } catch (e) {
      const msg = isError(e) ? e.Error : 'Failed to create preset';
      Notifications.addErrorNotification(msg);
      throw e;
    }
  };

  const handleApply = async (preset: Edge.Preset) => {
    if (Edge.isTeamPreset(preset)) {
      return;
    }

    try {
      await PresetApiService.applyLocalPreset(deviceId, {
        name: preset.name,
        sections: preset.sections,
      });
    } catch (e) {
      const msg = isError(e) ? e.Error : 'Failed to apply preset';
      Notifications.addErrorNotification(msg);
      throw e;
    }
  };

  const handleUploadToTeam = async (name: string) => {
    try {
      await PresetApiService.uploadUnitPreset(deviceId, name);
      await reloadTeamPresets();
    } catch (e) {
      const msg = isError(e) ? e.Error : 'Failed to upload preset';
      Notifications.addErrorNotification(msg);
      throw e;
    }
  };

  const handleDelete = async (preset: Edge.Preset) => {
    if (Edge.isTeamPreset(preset)) {
      return;
    }

    try {
      await PresetApiService.deleteLocalPreset(deviceId, preset.name);
      await reloadLocalPresets();
    } catch (e) {
      const msg = isError(e) ? e.Error : 'Failed to delete preset';
      Notifications.addErrorNotification(msg);
      throw e;
    }
  };

  return (
    <Box sx={sx}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={600}>
          Local presets on the {ModelService.isUnify(deviceModel) ? 'project' : 'device'}
        </Typography>

        {!isNil(capability) && (
          <>
            <Button
              data-id="create-preset-button"
              variant="outlined"
              color="primary"
              disabled={offline || creating}
              disableRipple={false}
              onClick={(e) => {
                if (!capability.enabled) {
                  setAnchorEl(e.currentTarget);
                  return;
                }

                setAnchorEl(null);
                setCreating(true);
              }}
            >
              Create a configuration preset
            </Button>
          </>
        )}
      </Stack>

      {!isNil(capability) && (
        <EdgePopover
          capability={capability}
          paymentAccess={user.role.canEditBilling()}
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
        />
      )}

      {!isSuccess && <LoadingPlaceholder />}

      {isSuccess && (
        <Box>
          {creating && (
            <DevicePresetForm
              sx={{mb: 2}}
              deviceName={deviceName}
              unitPresets={presets}
              offline={offline}
              model={deviceModel}
              onCancel={() => setCreating(false)}
              onCreate={handleCreatePreset}
            />
          )}

          <Stack gap={2}>
            {presets.map((preset) => (
              <PresetCard
                dataId={preset.name}
                key={preset.name}
                preset={preset}
                deviceName={deviceName}
                offline={offline}
                showModel={true}
                capability={unit.caps.presets}
                permitManage={user.role.canApplyPresets()}
                paymentAccess={user.role.canEditBilling()}
                savedAs={getNameInTeam(preset.hash)}
                onApply={handleApply}
                onUpload={handleUploadToTeam}
                onDelete={handleDelete}
              />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
